import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";

const L = {
  fr: { badge: "Un cadeau t'attend", title: "Bienvenue, cocooner", subtitle: "Trois curiosités pour allumer ta première étoile.", open: "Déballer le coffret", close: "Merci !", tap: "Appuie pour ouvrir" },
  en: { badge: "A gift awaits you", title: "Welcome, cocooner", subtitle: "Three curiosities to light your first star.", open: "Unwrap the box", close: "Thank you!", tap: "Tap to open" },
  es: { badge: "Un regalo te espera", title: "Bienvenido, cocooner", subtitle: "Tres curiosidades para encender tu primera estrella.", open: "Abrir el cofre", close: "¡Gracias!", tap: "Toca para abrir" },
  nl: { badge: "Een cadeau wacht op je", title: "Welkom, cocooner", subtitle: "Drie weetjes om je eerste ster te ontsteken.", open: "Pak het kistje uit", close: "Bedankt!", tap: "Tik om te openen" },
};

export function WelcomeGift() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const t = L[lang] || L.fr;
  const [show, setShow] = useState(false);
  const [opened, setOpened] = useState(false);
  const [curios, setCurios] = useState([]);

  useEffect(() => {
    api.get(`/welcome-gift?lang=${lang}`)
      .then(({ data }) => { if (data.available) { setCurios(data.curiosities || []); setShow(true); } })
      .catch(() => {});
  }, [lang]);

  const openGift = () => {
    setOpened(true);
    api.post("/welcome-gift/open").catch(() => {});
  };
  const dismiss = () => setShow(false);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-background/75 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="welcome-gift"
        >
          <motion.div
            initial={{ scale: 0.9, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="relative max-w-md w-full glass border border-primary/25 rounded-3xl p-8 text-center overflow-hidden"
          >
            {opened && <div className="gift-rays" aria-hidden />}
            <button onClick={dismiss} data-testid="gift-close" className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors">
              <X size={18} />
            </button>

            {!opened ? (
              <div className="relative">
                <p className="uppercase tracking-[0.22em] text-xs text-primary/90 mb-4">{t.badge}</p>
                <motion.button
                  onClick={openGift}
                  data-testid="gift-open-btn"
                  aria-label={t.open}
                  className="mx-auto mb-6 w-28 h-28 rounded-3xl flex items-center justify-center bg-primary/10 border border-primary/30 gift-box"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <Gift size={52} className="text-primary" />
                </motion.button>
                <h3 className="font-display italic text-3xl text-gradient-orange mb-2">{t.title}</h3>
                <p className="text-foreground/55 mb-6">{t.subtitle}</p>
                <button onClick={openGift} className="text-sm font-medium px-6 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  {t.open}
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center justify-center gap-2 mb-5">
                  <Sparkles className="text-primary pulse-star" size={20} />
                  <p className="uppercase tracking-[0.22em] text-xs text-primary/90">{t.title}</p>
                </div>
                <div className="space-y-3 mb-7 text-left">
                  {curios.map((c, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -14 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.25, duration: 0.5 }}
                      data-testid={`gift-curio-${i}`}
                      className="glass rounded-2xl px-4 py-3 border border-white/10 font-display italic text-foreground/85"
                    >
                      {c}
                    </motion.div>
                  ))}
                </div>
                <button onClick={dismiss} data-testid="gift-done-btn" className="text-sm font-medium px-6 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                  {t.close}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
